import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  first_name: "", last_name: "", gender: "", age: "", dob: "", phone: "", email: "", address: "", occupation: "",
  cause_of_amputation: "", date_of_amputation: "", patient_history: "",
  device_types: [], other_device: "", product_name: "", follow_up_date: "",
  referring_doctor: "", hospital_name: "", doctor_contact: "", department: "",
  blood_group: "", allergies: "", emergency_contact: "", notes: "",
};

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <p className="text-[11px] uppercase tracking-[0.15em] text-primary font-semibold border-b border-border pb-1.5">{title}</p>
      <div className="grid sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return <div className={`space-y-1.5 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;
}

export default function PatientDialog({ open, onOpenChange, patient, deviceTypes = [], onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const editing = Boolean(patient);

  useEffect(() => {
    if (open) setForm(patient ? { ...EMPTY, ...patient } : EMPTY);
  }, [open, patient]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDevice = (d) =>
    setForm((f) => ({ ...f, device_types: f.device_types.includes(d) ? f.device_types.filter((x) => x !== d) : [...f.device_types, d] }));

  const save = async () => {
    if (!form.first_name.trim()) { toast.error("First name is required"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/clinic/patients/${patient.id}`, form);
      else await api.post("/clinic/patients", form);
      toast.success(editing ? "Patient updated" : "Patient registered");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="patient-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit patient" : "New patient — Assessment & Enquiry"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-2">
          <Section title="Patient information">
            <Field label="First name"><Input data-testid="patient-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
            <Field label="Last name"><Input data-testid="patient-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
            <Field label="Gender">
              <Select value={form.gender || undefined} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger data-testid="patient-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Age"><Input data-testid="patient-age" value={form.age} onChange={(e) => set("age", e.target.value)} placeholder="e.g. 45" /></Field>
            <Field label="Contact no"><Input data-testid="patient-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+234…" /></Field>
            <Field label="Email"><Input type="email" data-testid="patient-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Used for portal login" /></Field>
            <Field label="Occupation"><Input data-testid="patient-occupation" value={form.occupation} onChange={(e) => set("occupation", e.target.value)} /></Field>
            <Field label="Date of birth"><Input type="date" data-testid="patient-dob" value={form.dob} onChange={(e) => set("dob", e.target.value)} /></Field>
            <Field label="Address" className="sm:col-span-2"><Textarea data-testid="patient-address" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          </Section>

          <Section title="Clinical details">
            <Field label="Cause of amputation"><Input data-testid="patient-cause" value={form.cause_of_amputation} onChange={(e) => set("cause_of_amputation", e.target.value)} placeholder="e.g. Trauma, diabetes" /></Field>
            <Field label="Date of amputation"><Input type="date" data-testid="patient-amp-date" value={form.date_of_amputation} onChange={(e) => set("date_of_amputation", e.target.value)} /></Field>
            <Field label="Patient history" className="sm:col-span-2"><Textarea data-testid="patient-history" rows={2} value={form.patient_history} onChange={(e) => set("patient_history", e.target.value)} /></Field>
          </Section>

          <Section title="Assessment details">
            <div className="sm:col-span-2 space-y-2">
              <Label className="text-xs">Type of device required</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {deviceTypes.map((d) => (
                  <label key={d} data-testid={`device-${d}`} className="flex items-center gap-2 text-sm cursor-pointer rounded-md border border-border px-2.5 py-1.5 hover:bg-accent/50">
                    <Checkbox checked={form.device_types.includes(d)} onCheckedChange={() => toggleDevice(d)} />
                    <span>{d}</span>
                  </label>
                ))}
              </div>
            </div>
            <Field label="Other assistive device"><Input data-testid="patient-other-device" value={form.other_device} onChange={(e) => set("other_device", e.target.value)} placeholder="Specify if any" /></Field>
            <Field label="Product name"><Input data-testid="patient-product" value={form.product_name} onChange={(e) => set("product_name", e.target.value)} /></Field>
            <Field label="Follow-up date"><Input type="date" data-testid="patient-followup" value={form.follow_up_date} onChange={(e) => set("follow_up_date", e.target.value)} /></Field>
          </Section>

          <Section title="Referring doctor's details">
            <Field label="Doctor name"><Input data-testid="patient-ref-doctor" value={form.referring_doctor} onChange={(e) => set("referring_doctor", e.target.value)} /></Field>
            <Field label="Hospital name"><Input data-testid="patient-hospital" value={form.hospital_name} onChange={(e) => set("hospital_name", e.target.value)} /></Field>
            <Field label="Department"><Input data-testid="patient-department" value={form.department} onChange={(e) => set("department", e.target.value)} /></Field>
            <Field label="Doctor contact no"><Input data-testid="patient-doctor-contact" value={form.doctor_contact} onChange={(e) => set("doctor_contact", e.target.value)} /></Field>
          </Section>

          <Section title="Other (optional)">
            <Field label="Blood group"><Input data-testid="patient-blood" value={form.blood_group} onChange={(e) => set("blood_group", e.target.value)} /></Field>
            <Field label="Emergency contact"><Input data-testid="patient-emergency" value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} /></Field>
            <Field label="Allergies" className="sm:col-span-2"><Input data-testid="patient-allergies" value={form.allergies} onChange={(e) => set("allergies", e.target.value)} /></Field>
            <Field label="Notes" className="sm:col-span-2"><Textarea data-testid="patient-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          </Section>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-patient-button" className="bg-primary hover:bg-primary/90">
            {busy ? "Saving…" : editing ? "Save changes" : "Register patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
